import { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, CircularProgress, Alert,
  Chip, MenuItem, Select, FormControl, InputLabel, Divider,
  Avatar, Tooltip, IconButton,
} from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  PersonRemove as RemovedIcon,
  AdminPanelSettings as PromotedIcon,
  Event as EventCreatedIcon,
  EventBusy as EventDeletedIcon,
  HowToReg as RegisteredIcon,
  PersonOff as CancelledIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@hooks/useAuth';
import { getActivityLogs, getAllActivityLogs, LOG_LABELS } from '@services/activityLogService';
import { getAllOrganizations } from '@services/organizationService';

const TYPE_META = {
  user_approved:          { icon: ApprovedIcon,      color: 'success.main' },
  user_rejected:          { icon: RejectedIcon,       color: 'error.main' },
  user_removed:           { icon: RemovedIcon,        color: 'error.main' },
  user_promoted:          { icon: PromotedIcon,       color: 'info.main' },
  event_created:          { icon: EventCreatedIcon,   color: 'primary.main' },
  event_deleted:          { icon: EventDeletedIcon,   color: 'warning.main' },
  registration_created:   { icon: RegisteredIcon,     color: 'success.main' },
  registration_cancelled: { icon: CancelledIcon,      color: 'text.secondary' },
};

const formatTs = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return formatDistanceToNow(date, { addSuffix: true });
};

const logDescription = (log) => {
  const label = LOG_LABELS[log.type] || log.type;
  if (log.targetName) {
    if (log.type.startsWith('event_')) return `${label}: "${log.targetName}"`;
    if (log.type.startsWith('registration_')) return `${label}: "${log.targetName}"`;
    return `${label}: ${log.targetName}`;
  }
  return label;
};

const ActivityPage = () => {
  const { userData, isOrgAdmin } = useAuth();
  const isPlatformAdmin = userData?.userType === 'platform-admin';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('');
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    if (isPlatformAdmin) {
      getAllOrganizations()
        .then((orgs) => setOrganizations(orgs.filter((o) => o.status === 'active')))
        .catch(() => {});
    }
  }, [isPlatformAdmin]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      let data;
      if (isPlatformAdmin) {
        data = await getAllActivityLogs(30, orgFilter || null);
      } else {
        data = await getActivityLogs(userData.organizationId);
      }
      setLogs(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) loadLogs();
  }, [userData, orgFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = typeFilter === 'all' ? logs : logs.filter((l) => l.type === typeFilter);

  const typeOptions = [
    { value: 'all', label: 'All activity' },
    ...Object.entries(LOG_LABELS).map(([value, label]) => ({ value, label })),
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Activity Log</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isPlatformAdmin ? 'Activity across all organizations' : `Activity for ${userData?.organizationName}`}
            {' · '}last 30 days
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadLogs} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Activity type</InputLabel>
          <Select value={typeFilter} label="Activity type" onChange={(e) => setTypeFilter(e.target.value)}>
            {typeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {isPlatformAdmin && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Organization</InputLabel>
            <Select value={orgFilter} label="Organization" onChange={(e) => setOrgFilter(e.target.value)}>
              <MenuItem value="">All organizations</MenuItem>
              {organizations.map((o) => (
                <MenuItem key={o.organizationId} value={o.organizationId}>{o.organizationName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No activity found for this period.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {filtered.map((log, idx) => {
            const meta = TYPE_META[log.type] || { icon: EventCreatedIcon, color: 'text.secondary' };
            const IconComp = meta.icon;
            return (
              <Box key={log.id}>
                {idx > 0 && <Divider />}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Avatar sx={{ bgcolor: 'grey.100', width: 36, height: 36, flexShrink: 0, mt: 0.25 }}>
                    <IconComp sx={{ fontSize: 20, color: meta.color }} />
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {logDescription(log)}
                      </Typography>
                      {isPlatformAdmin && log.organizationName && (
                        <Chip label={log.organizationName} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      by {log.actorName || 'System'}
                      {log.metadata?.assignmentName ? ` · ${log.metadata.assignmentName}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: 0.25 }}>
                    {formatTs(log.timestamp)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}
    </Container>
  );
};

export default ActivityPage;
